import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { configureLegacyNavigation, configureNavigation } from '@m3-tech/boss-utils'
import { NAVIGATION_REGISTRY } from '@m3-tech/boss-ui'
// All Mantine packages except `@mantine/hooks` require styles imports
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/dropzone/styles.css'
import '@mantine/tiptap/styles.css'
import '@mantine/charts/styles.css'
import '@m3-tech/boss-ui/styles.css'
import '@m3-tech/boss-form-components/styles.css'
// Email-template editor styles (bundles the GrapesJS canvas/chrome CSS).
import '@m3-tech/boss-deals/styles.css'
// Task-template content editor styles (ContentEditorTabs.module.css). Without
// this the CSS-module class names still render but resolve to nothing (AB#1772).
import '@m3-tech/boss-tasks/styles.css'
import './index.css'
import { assertRequiredEnvVars } from './core/assertRequiredEnvVars'
import { I18nApp } from './I18nApp'

// Fail fast with a clear message if the environment isn't configured —
// previously this manifested as an infinite spinner during AuthWrapper bootstrap.
assertRequiredEnvVars()

// Setup dayjs for Mantine DateInput custom parse format
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import 'dayjs/locale/fr.js'
dayjs.extend(customParseFormat)

// Initialize shared navigation utilities with module-specific config
configureLegacyNavigation(import.meta.env.VITE_LEGACY_APP_URL ?? import.meta.env.VITE_API_URL ?? '')
configureNavigation({
    currentModuleKey: import.meta.env.VITE_MODULE_NAME?.toLowerCase() ?? 'settings',
    registry: NAVIGATION_REGISTRY,
    talentLmsConfig: {
        apiKey: import.meta.env.VITE_TALENT_LMS_API_KEY ?? '',
        domain: import.meta.env.VITE_TALENT_LMS_DOMAIN ?? '',
        branchEn: import.meta.env.VITE_TALENT_LMS_BRANCH_EN ?? '',
        branchFr: import.meta.env.VITE_TALENT_LMS_BRANCH_FR ?? '',
        endpoint: import.meta.env.VITE_TALENT_LMS_ENDPOINT ?? '',
        corsProxy:
            import.meta.env.VITE_TALENT_LMS_PROXY ||
            import.meta.env.VITE_TALENT_LMS_CORS_PROXY ||
            '',
    },
    lenderMatchUrl: import.meta.env.VITE_LENDER_MATCH_URL ?? '',
    scenarioBuilderUrl: import.meta.env.VITE_SCENARIO_BUILDER_URL ?? '',
    modernModuleUrls: Object.fromEntries(
        Object.entries(import.meta.env)
            .filter(([k]) => /^VITE_\w+_MODULE_URL$/.test(k))
            .map(([k, v]) => [k.replace(/^VITE_|_MODULE_URL$/g, '').toLowerCase(), v]),
    ),
})

// Log environment info on app start
console.info(
    `[App] Environment: ${import.meta.env.VITE_NODE_ENV} | Version: ${import.meta.env.VITE_APP_VERSION}`,
)

const root = createRoot(document.getElementById('root')!)

/**
 * Enable MSW (Mock Service Worker) for API mocking in development.
 * Set VITE_ENABLE_MSW=true in .env to enable during local dev.
 *
 * Guarded by import.meta.env.DEV so Vite completely eliminates MSW from
 * all production/staging/QA builds (dead code elimination). See docs/SECURITY.md [BUILD-1].
 */
async function enableMocking(): Promise<void> {
    if (!import.meta.env.DEV || import.meta.env.VITE_ENABLE_MSW !== 'true') {
        // A previous dev session may have registered the MSW service worker;
        // it sticks around across reloads and will keep intercepting fetches
        // even after VITE_ENABLE_MSW is flipped off. Unregister any leftover
        // MSW worker so real API calls go through.
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations()
            await Promise.all(
                registrations
                    .filter((r) => r.active?.scriptURL.includes('mockServiceWorker'))
                    .map((r) => r.unregister()),
            )
        }
        return
    }

    const { worker } = await import('./mocks/browser')
    await worker.start({
        onUnhandledRequest: 'bypass', // Don't warn for unhandled requests
    })
}

// Ensure MSW is ready before rendering the app
enableMocking().then(() => {
    root.render(
        <StrictMode>
            <I18nApp />
        </StrictMode>,
    )
})
